import * as THREE from 'three';
import type { EnemyState, HeroState, MapDefinition, Point, UnitState } from '../../../shared/game/world-types.js';
import { sees, viewHeading } from '../../../shared/game/sight-lines.js';
import { groundDecal, groundUi } from './ground-decal.js';

/** The shaded ground round a sneaking hero: its half-width and cell, in metres. Cells snap to a fixed grid so the shade never swims. */
const REACH = 22, CELL = .75, CELLS = Math.round(REACH * 2 / CELL);
/** Ground a foe in the fight can see (its cone and near circle): a warm wash. Ground out of every foe's sight: a cool shadow. Both fade out toward the edge. */
const SEEN = new THREE.Color(0xd9a55a), SEEN_ALPHA = .26, SAFE = new THREE.Color(0x1f2b40), SAFE_ALPHA = .3;
/** Seconds between recomputing the shade while nothing moved far, how far a hero or foe moves, and how far a foe turns (radians), before it is recomputed sooner. */
const REFRESH = .25, MOVED = .35, TURNED = .12;
/** How far above the ground the shade sits, as the other ground decals do. */
const LIFT = .06;

type Viewer = Point & { level?: number; heading?: number };

/**
 * What a sneaking hero needs to read, painted on the ground round the selected one in a fight from the snapshot and the same rule the
 * server hides it by (`sees`: a foe's frontal cone and near circle, with a clear line): ground a foe can see in a warm wash, the rest in
 * a cool shadow, so each foe's cone shows where it looks. `exposed` lets the route marks warn where a move steps into sight. No glyphs:
 * whether a hero is hidden shows on the hero itself (its crouch and the shadow veil, `status-effects.ts`). Presentation only.
 */
export class StealthMarks {
  readonly shade: THREE.Mesh;
  private positions: Float32Array;
  private colors: Float32Array;
  /** Foes in the fight as of the last recompute, and where the shade was centred then. */
  private viewers: Viewer[] = [];
  private centre: Point | null = null;
  private computedAt = -Infinity;
  private seen: Uint8Array;
  private origin = { x: 0, z: 0 };

  constructor(private scene: THREE.Scene, private map: MapDefinition, private ground: (x: number, z: number) => number) {
    const side = CELLS + 1;
    this.positions = new Float32Array(side * side * 3); this.colors = new Float32Array(side * side * 4); this.seen = new Uint8Array(side * side);
    const indices: number[] = [];
    for (let row = 0; row < CELLS; row++) for (let column = 0; column < CELLS; column++) {
      const a = row * side + column, b = a + 1, c = a + side, d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 4).setUsage(THREE.DynamicDrawUsage));
    geometry.setIndex(indices);
    this.shade = groundUi(groundDecal(new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, toneMapped: false }))));
    this.shade.frustumCulled = false; this.shade.visible = false;
    scene.add(this.shade);
  }

  /** `focus` is the selected hero holding Hide in a fight, if any: the shade follows it. `figures` places it where the hero is drawn this frame. `seconds` is wall-clock time. */
  update(units: readonly UnitState[], enemies: readonly EnemyState[], focus: HeroState | undefined, figures: ReadonlyMap<string, THREE.Object3D>, seconds: number) {
    const engaged = enemies.filter(enemy => enemy.engaged && !enemy.health.dead && !enemy.fleeing);
    if (!focus || !engaged.length) { this.shade.visible = false; this.centre = null; this.viewers = []; return; }
    const viewers = engaged.map((enemy): Viewer => ({ x: enemy.x, z: enemy.z, ...enemy.level ? { level: enemy.level } : {},
      heading: viewHeading(enemy, units.find(unit => unit.id === enemy.combat.targetId)) }));
    const figure = figures.get(focus.id), at = figure ? { x: figure.position.x, z: figure.position.z } : focus;
    const turned = (a?: number, b?: number) => a === undefined || b === undefined ? a !== b : Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b))) > TURNED;
    const changed = !this.centre || Math.hypot(at.x - this.centre.x, at.z - this.centre.z) > MOVED || viewers.length !== this.viewers.length
      || viewers.some((viewer, i) => Math.hypot(viewer.x - this.viewers[i].x, viewer.z - this.viewers[i].z) > MOVED || turned(viewer.heading, this.viewers[i].heading));
    if (changed || seconds - this.computedAt > REFRESH) this.recompute(at, focus.level, viewers, seconds);
    this.shade.visible = true;
  }

  /** Whether a hero standing here would be seen by a foe in the fight: the warning on a sneaking hero's route. */
  exposed(point: Point & { level?: number }): boolean {
    if (!this.viewers.length) return false;
    const column = Math.round((point.x - this.origin.x) / CELL), row = Math.round((point.z - this.origin.z) / CELL);
    if (column >= 0 && row >= 0 && column <= CELLS && row <= CELLS) return this.seen[row * (CELLS + 1) + column] === 1;
    return this.viewers.some(viewer => sees(viewer, viewer.heading, point, this.map));
  }

  dispose() { this.shade.geometry.dispose(); (this.shade.material as THREE.Material).dispose(); this.scene.remove(this.shade); }

  private recompute(at: Point, level: number | undefined, viewers: Viewer[], seconds: number) {
    this.centre = { ...at }; this.computedAt = seconds; this.viewers = viewers;
    const side = CELLS + 1;
    this.origin.x = Math.round(at.x / CELL) * CELL - REACH; this.origin.z = Math.round(at.z / CELL) * CELL - REACH;
    for (let row = 0; row < side; row++) for (let column = 0; column < side; column++) {
      const i = row * side + column, x = this.origin.x + column * CELL, z = this.origin.z + row * CELL;
      const point = { x, z, ...level ? { level } : {} };
      const seen = viewers.some(viewer => sees(viewer, viewer.heading, point, this.map));
      this.seen[i] = seen ? 1 : 0;
      // Round, so the shade reads as the hero's surroundings rather than a square.
      const edge = Math.max(0, 1 - Math.max(0, Math.hypot(x - at.x, z - at.z) - REACH * .6) / (REACH * .4));
      const color = seen ? SEEN : SAFE, alpha = (seen ? SEEN_ALPHA : SAFE_ALPHA) * edge;
      this.positions[i * 3] = x; this.positions[i * 3 + 1] = this.ground(x, z) + LIFT; this.positions[i * 3 + 2] = z;
      this.colors[i * 4] = color.r; this.colors[i * 4 + 1] = color.g; this.colors[i * 4 + 2] = color.b; this.colors[i * 4 + 3] = alpha;
    }
    const geometry = this.shade.geometry;
    geometry.getAttribute('position').needsUpdate = true; geometry.getAttribute('color').needsUpdate = true;
  }
}
